import GameLivesBadge from '../features/games/GameLivesBadge';
import { Gamepad2 } from "lucide-react";
import { gamesPracticeEnabled } from "../features/games/access";
import { accessStatus } from "../lib/freePlan";
import FreePlanUsage from "../components/FreePlanUsage";
import WorkspaceInvitationDialog from "../components/WorkspaceInvitationDialog";
import WorkspaceSelector, { WorkspaceStatus } from "../components/WorkspaceSelector";
import { useWorkspaces } from "../contexts/WorkspaceContext";
import { useState, useEffect, useMemo } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { api, useAuth } from "../App";
import ProfileSettings from "../components/ProfileSettings";
import FloatingAIChat from "../components/FloatingAIChat";
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import ReferralPopover from "../components/ReferralPopover";
import { Switch } from "../components/ui/switch";
import { toast } from "@/lib/toast";

const ALL_NAV_ITEMS = [
  { id: "games", path: "/dashboard/juegos" },
  { id: "dashboard",     path: "/dashboard",               end: true },
  { id: "transactions",  path: "/dashboard/transactions" },
  { id: "credit-cards",  path: "/dashboard/credit-cards" },
  { id: "subscriptions", path: "/dashboard/subscriptions" },
  { id: "trips",         path: "/dashboard/trips" },
  { id: "assets",        path: "/dashboard/assets" },
  { id: "planes",        path: "/dashboard/planes" },
  { id: "reminders",     path: "/dashboard/reminders" },
  { id: "habits",        path: "/dashboard/habits" },
  { id: "debts",         path: "/dashboard/debts" },
  { id: "savings",       path: "/dashboard/savings" },
  { id: "shared",        path: "/dashboard/shared" },
];

/* ─── SVG icons matching Figma using currentColor ─── */
const IconHome = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 20V14H14V20H19V12H22L12 3L2 12H5V20H10Z" />
  </svg>
);
const IconMovimientos = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4V1L8 5L12 9V6C15.31 6 18 8.69 18 12C18 13.01 17.75 13.97 17.3 14.8L18.76 16.26C19.54 15.03 20 13.57 20 12C20 7.58 16.42 4 12 4ZM12 18C8.69 18 6 15.31 6 12C6 10.99 6.25 10.03 6.7 9.2L5.24 7.74C4.46 8.97 4 10.43 4 12C4 16.42 7.58 20 12 20V23L16 19L12 15V18Z" fill="currentColor"/>
  </svg>
);
const IconTarjetas = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
    <path d="M2 10H22" stroke="currentColor" strokeWidth="2"/>
    <path d="M6 15H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconSuscripciones = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 11C17.66 11 18.99 9.66 18.99 8C18.99 6.34 17.66 5 16 5C14.34 5 13 6.34 13 8C13 9.66 14.34 11 16 11ZM8 11C9.66 11 10.99 9.66 10.99 8C10.99 6.34 9.66 5 8 5C6.34 5 5 6.34 5 8C5 9.66 6.34 11 8 11ZM8 13C5.67 13 1 14.17 1 16.5V19H15V16.5C15 14.17 10.33 13 8 13ZM16 13C15.71 13 15.38 13.02 15.03 13.05C16.19 13.89 17 15.02 17 16.5V19H23V16.5C23 14.17 18.33 13 16 13Z" />
  </svg>
);
const IconViajes = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 16V14L13 9V3.5C13 2.67 12.33 2 11.5 2C10.67 2 10 2.67 10 3.5V9L2 14V16L10 13.5V19L8 20.5V22L11.5 21L15 22V20.5L13 19V13.5L21 16Z" />
  </svg>
);
const IconMiPlan = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5ZM19 19C19 19.55 18.55 20 18 20H6C5.45 20 5 19.55 5 19V18H19V19Z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round"/>
  </svg>
);
const IconRecordatorios = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="2"/>
    <path d="M16 2V6M8 2V6M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconHabitos = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconDeudas = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
    <path d="M4 20C4 16.69 7.58 14 12 14C16.42 14 20 16.69 20 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 20H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconAhorros = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M19.5 12C19.5 7.86 16.14 4.5 12 4.5C10.29 4.5 8.72 5.06 7.47 6H5V8.47C3.88 9.72 3 11.29 3 13C3 16.5 5.5 19.5 9 20.4V22H15V20.4C17.5 19.5 19.5 16.5 19.5 14H21.5C21.5 12.67 21.5 12 19.5 12ZM15 14C15 14 13.5 16 12 16C10.5 16 9 14 9 14V12H15V14Z" stroke="currentColor" strokeWidth="1.8" fill="none"/>
  </svg>
);
const IconActivos = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <polyline points="22,7 13.5,15.5 8.5,10.5 2,17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="16,7 22,7 22,13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconCompartidos = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="2"/>
    <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
    <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="2"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke="currentColor" strokeWidth="2"/>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke="currentColor" strokeWidth="2"/>
  </svg>
);
const IconChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const STREAK_FIRE_ASSET = "/racha-fuego-3d.webp";

const StreakBadge = ({ profile, compact = false, preferenceLoading = false, onParticipationChange }) => {
  const streakDays = Math.max(0, Number(profile?.current_streak) || 0);
  const longestStreak = Math.max(streakDays, Number(profile?.longest_streak) || 0);
  const available = Boolean(
    (profile?.habit_eligible ?? profile?.subscription_eligible) &&
    profile?.status !== "globally_disabled" &&
    profile?.status !== "subscription_inactive"
  );
  const participationEnabled = Boolean(profile?.participation_enabled);

  if (!available) return null;

  const completedToday = Boolean(profile?.qualified_today);
  const statusMessage = !participationEnabled
    ? "Tu historial está guardado. Mientras estén apagadas no recibirás recordatorios, pero el calendario seguirá avanzando."
    : !profile?.timezone_resolved
      ? "Confirma tu zona horaria para comenzar a registrar tu minuto financiero diario."
      : profile?.reward_today
        ? "Tu recompensa está aplicada. Continúa mañana para sumar otro día."
      : completedToday
        ? "Tu minuto financiero de hoy está completo."
        : streakDays > 0
          ? "Completa tu minuto financiero de hoy para protegerla."
          : profile?.last_lost_streak > 0
            ? "Tu racha se interrumpió. Completa tu minuto financiero para retomarla."
            : "Completa tu primer minuto financiero para iniciar la racha.";
  const badgeLabel = participationEnabled ? `x${streakDays}` : "Off";
  const accessibilityLabel = participationEnabled
    ? `Racha actual: ${streakDays} ${streakDays === 1 ? "día" : "días"}. Administrar rachas.`
    : "Rachas desactivadas. Administrar rachas.";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`aorra-streak-badge${compact ? " aorra-streak-badge-compact" : ""}${participationEnabled ? "" : " aorra-streak-badge-disabled"}`}
          title={accessibilityLabel}
          aria-label={accessibilityLabel}
          data-testid={compact ? "streak-badge-mobile" : "streak-badge-desktop"}
        >
          <img src={STREAK_FIRE_ASSET} alt="" aria-hidden="true" draggable="false" />
          <span aria-hidden="true">{badgeLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="mt-2 w-80 max-w-[calc(100vw-32px)] max-h-[min(85dvh,var(--radix-popover-content-available-height))] overflow-y-auto rounded-[20px] border border-[#E1E9FC] bg-white p-5 shadow-[-2px_0px_30px_rgba(176,179,189,0.22)]"
        align="end"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#F4F7FF]">
            <img
              src={STREAK_FIRE_ASSET}
              alt=""
              aria-hidden="true"
              draggable="false"
              className="h-12 w-12 object-contain"
            />
          </div>
          <div>
            <p className="font-['Manrope'] text-xs font-extrabold uppercase tracking-[0.1em] text-[#396BE9]">
              Racha financiera
            </p>
            <p className="mt-1 font-['Manrope'] text-xl font-extrabold text-[#1B2559]">
              {participationEnabled
                ? `${streakDays} ${streakDays === 1 ? "día" : "días"}`
                : "Desactivadas"}
            </p>
          </div>
        </div>
        <p className="mt-4 font-['Manrope'] text-sm font-medium leading-relaxed text-[#64748B]">
          {statusMessage}
        </p>
        <div className="mt-4 flex items-center justify-between rounded-xl bg-[#F4F7FF] px-4 py-3 font-['Manrope'] text-xs">
          <span className="font-bold text-[#64748B]">Récord histórico</span>
          <span className="font-extrabold text-[#396BE9]">{longestStreak} {longestStreak === 1 ? "día" : "días"}</span>
        </div>
        {(profile?.bonus_days > 0 || profile?.restored_days > 0) && <p className="mt-2 text-[11px] text-slate-500">{profile.activity_days || 0} de actividad · {profile.bonus_days || 0} extra · {profile.restored_days || 0} recuperados</p>}
        <ReferralPopover />
        <div className="mt-3 flex items-center justify-between gap-4 rounded-xl border border-[#E1E9FC] bg-[#FAFBFF] px-4 py-3">
          <div>
            <label htmlFor={compact ? "streak-toggle-mobile" : "streak-toggle-desktop"} className="block font-['Manrope'] text-sm font-extrabold text-[#1B2559]">
              Rachas activas
            </label>
            <p className="mt-1 font-['Manrope'] text-xs font-medium leading-snug text-[#64748B]">
              Incluye recordatorios por WhatsApp.
            </p>
          </div>
          <Switch
            id={compact ? "streak-toggle-mobile" : "streak-toggle-desktop"}
            checked={participationEnabled}
            onCheckedChange={onParticipationChange}
            disabled={preferenceLoading || typeof onParticipationChange !== "function"}
            className="focus-visible:ring-[#396BE9]/30 data-[state=checked]:bg-[#396BE9] data-[state=unchecked]:bg-[#D7E1F5]"
            aria-label="Activar o desactivar rachas"
            data-testid={compact ? "streak-toggle-mobile" : "streak-toggle-desktop"}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

/* ─── Map of icons per route id ─── */
const NAV_ICONS = {
  games: { Icon: Gamepad2, label: "Juegos", showChevron: false },
  dashboard:    { Icon: IconHome,          label: "Inicio",         showChevron: false },
  transactions: { Icon: IconMovimientos,   label: "Movimientos",    showChevron: false },
  "credit-cards":{ Icon: IconTarjetas,     label: "Cuentas y tarjetas",       showChevron: false },
  subscriptions:{ Icon: IconSuscripciones, label: "Suscripciones",  showChevron: false },
  trips:        { Icon: IconViajes,        label: "Viajes",         showChevron: false },
  planes:       { Icon: IconMiPlan,        label: "Mi plan",        showChevron: true  },
  reminders:    { Icon: IconRecordatorios, label: "Recordatorios",  showChevron: false },
  habits:       { Icon: IconHabitos,       label: "Hábitos",        showChevron: false },
  debts:        { Icon: IconDeudas,        label: "Mis Deudas",     showChevron: false },
  savings:      { Icon: IconAhorros,       label: "Metas Ahorro",   showChevron: false },
  assets:       { Icon: IconActivos,       label: "Activos",        showChevron: false },
  shared:       { Icon: IconCompartidos,   label: "Compartidos",    showChevron: false },
};

/* ═══════════════════════════════════════════════════════
   DASHBOARD LAYOUT
═══════════════════════════════════════════════════════ */
const DashboardLayout = () => {
  const { user: identityUser, logout, appSettings } = useAuth();
  const workspace = useWorkspaces();
  const user = useMemo(() => workspace?.active ? { ...identityUser, currency: workspace.active.currency } : identityUser, [identityUser, workspace?.active]);
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [showExpiredAlert, setShowExpiredAlert] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [healthScore, setHealthScore] = useState(null);
  const [streakProfile, setStreakProfile] = useState(null);
  const [streakPreferenceLoading, setStreakPreferenceLoading] = useState(false);

  // Dashboard artwork is decorative. Disable browser image save/drag affordances
  // while keeping the surrounding cards and buttons fully interactive.
  useEffect(() => {
    document.body.classList.add("aorra-portal");
    const blockImageMenu = (event) => {
      if (event.target instanceof HTMLImageElement) event.preventDefault();
    };
    document.addEventListener("contextmenu", blockImageMenu);
    document.addEventListener("dragstart", blockImageMenu);

    return () => {
      document.body.classList.remove("aorra-portal");
      document.removeEventListener("contextmenu", blockImageMenu);
      document.removeEventListener("dragstart", blockImageMenu);
    };
  }, []);

  // Lock body scroll on viewport level to prevent iOS scroll translation bugs when focusing inputs
  useEffect(() => {
    document.body.classList.add("dashboard-body-lock");
    document.documentElement.classList.add("dashboard-body-lock");
    return () => {
      document.body.classList.remove("dashboard-body-lock");
      document.documentElement.classList.remove("dashboard-body-lock");
    };
  }, []);

  // The dashboard page supplies this score after loading its existing summary.
  // Do not issue another summary request from the persistent layout on every
  // portal navigation.

  const scoreLabel = (healthScore ?? 0) >= 80 ? "Excelente" : (healthScore ?? 0) >= 50 ? "Regular" : "Crítica";
  const scoreColor = (healthScore ?? 0) >= 80 ? "#22C55E" : (healthScore ?? 0) >= 50 ? "#F59E0B" : "#EF4444";

  /* ── Subscription ── */
  const subscriptionStatus = useMemo(() => accessStatus(user), [user]);

  const handleStreakParticipationChange = async (enabled) => {
    setStreakPreferenceLoading(true);
    try {
      const response = await api.put("/streaks/me/settings", { enabled });
      setStreakProfile(response.data);
      window.dispatchEvent(new CustomEvent("aorra:streak-updated", { detail: response.data }));
      toast.success(enabled ? "Rachas activadas" : "Rachas y recordatorios desactivados");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo actualizar la preferencia de rachas");
    } finally {
      setStreakPreferenceLoading(false);
    }
  };

  useEffect(() => {
    if (subscriptionStatus.isExpired && !workspace?.active?.writable) setShowExpiredAlert(true);
  }, [subscriptionStatus.isExpired, workspace?.active?.writable]);

  useEffect(() => {
    // Always ask the authenticated streak endpoint for the source of truth.
    // The returned profile already enforces the global switch, subscription,
    // participation and timezone requirements. Gating this request on the
    // separately loaded public settings could leave the badge hidden when
    // that request was delayed or the browser still held older state.
    // An administrator may also use the customer portal with the same paid
    // account. Eligibility comes from the streak profile, not from the role.
    if (!user) {
      setStreakProfile(null);
      return undefined;
    }

    let active = true;
    const loadStoredStreak = async () => {
      try {
        const response = await api.get("/streaks/me", { params: { refresh: false } });
        if (active) setStreakProfile(response.data);
      } catch (_error) {
        // The live refresh below remains the second source of truth. Keep the
        // header stable instead of hiding an already loaded profile.
      }
    };
    const loadStreak = async (refresh = true) => {
      try {
        const response = await api.get("/streaks/me", { params: { refresh } });
        if (active) setStreakProfile(response.data);
      } catch (_error) {
        // A transient reconciliation error must not erase the last valid
        // profile already returned by the server.
      }
    };
    const handleFocus = () => loadStreak(true);
    const handleStreakUpdate = (event) => {
      if (active && event.detail) setStreakProfile(event.detail);
    };

    loadStoredStreak();
    loadStreak(true);
    const interval = window.setInterval(loadStoredStreak, 30000);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("aorra:streak-updated", handleStreakUpdate);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("aorra:streak-updated", handleStreakUpdate);
    };
  }, [user]);

  /* Close mobile menu on route change */
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  /* ── Nav items (ordered as Figma) ── */
  const hiddenModules = useMemo(
    () => appSettings?.hidden_modules || [],
    [appSettings?.hidden_modules],
  );
  const navItems = ALL_NAV_ITEMS.filter(item => item.id === "games" ? gamesPracticeEnabled(appSettings) : !hiddenModules.includes(item.id));

  /* Redirect if accessing hidden route */
  useEffect(() => {
    const hidden = ALL_NAV_ITEMS.find(
      item => (item.id === "games" ? !gamesPracticeEnabled(appSettings) : hiddenModules.includes(item.id)) && location.pathname.startsWith(item.path)
    );
    if (hidden) navigate("/dashboard", { replace: true });
  }, [location.pathname, hiddenModules, appSettings, navigate]);

  const firstName = user?.name?.split(" ")[0] || "Usuario";

  /* Last access timestamp */
  const lastAccess = useMemo(() => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");
    return `${mm}-${dd}-${yyyy} | ${hh}:${min}:${ss}`;
  }, []);

  return (
    <div style={{ height: "100vh", overflow: "hidden", display: "flex", flexDirection: "column", background: "#F5F5F5", fontFamily: "'Manrope', 'Inter', sans-serif" }}>

      {/* ══════════════════════════════════════════════
          EXPIRED ALERT
      ══════════════════════════════════════════════ */}
      <AlertDialog open={showExpiredAlert} onOpenChange={setShowExpiredAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: "#ef4444" }}>⚠️ Suscripción Expirada</AlertDialogTitle>
            <AlertDialogDescription>
              Tu periodo de acceso gratuito ha terminado. Puedes ver tu información financiera pero no podrás hacer cambios.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button onClick={() => setShowExpiredAlert(false)} style={{ background: "#396BE9", color: "#fff" }}>
              Entendido
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ══════════════════════════════════════════════
          HEADER — DESKTOP (full width, rounded bottom-left 72px)
      ══════════════════════════════════════════════ */}
      <header className="aorra-header aorra-header-desktop" style={{
        position:     "relative",
        width:        "100%",
        background:   "#FFFFFF",
        boxShadow:    "0px 4px 8px rgba(47, 95, 151, 0.05)",
        borderRadius: "0px 0px 0px 72px",
        zIndex:       20,
        flexShrink:   0,
      }}>
        <div style={{
          display:       "flex",
          flexDirection: "row",
          alignItems:    "center",
          padding:       "0px 20px",
          height:        88,
          width:         "100%",
          position:      "relative",
        }}>
          {/* Logo area (224px — same as sidebar) */}
          <div style={{ width: 224, height: 88, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <img src="/logo-claro.png" alt="Aorra" style={{ height: 32, objectFit: "contain" }} />
          </div>

          {/* Greeting, avatar and board selector share the available space. */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            minWidth: 0,
            margin: "0 20px",
            gap: 14,
          }}>
            <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 24, color: "#2B50AF", whiteSpace: "nowrap", minWidth: 0, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>
              ¡Hola {firstName}!
            </span>
            {/* Avatar */}
            <button onClick={() => setProfileOpen(true)} title="Mi perfil" style={{
              width: 46, height: 46, borderRadius: "50%", background: "#CDF7F7",
              border: "none", cursor: "pointer", display: "flex", alignItems: "center",
              justifyContent: "center", fontFamily: "'Manrope', sans-serif",
              fontWeight: 700, fontSize: 18, color: "#2B50AF", flexShrink: 0,
              overflow: "hidden", padding: 0
            }}>
              {user?.avatar ? (
                <img src={`/avatares/${user.avatar}`} alt="Avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                user?.name?.charAt(0).toUpperCase() || "U"
              )}
            </button>
            <WorkspaceSelector />
          </div>

          {/* Right: streak + logout icons */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 20 }}>
            <GameLivesBadge enabled={appSettings?.games?.public_enabled === true} />
            <StreakBadge
              profile={appSettings?.streaks_enabled === true ? streakProfile : null}
              preferenceLoading={streakPreferenceLoading}
              onParticipationChange={handleStreakParticipationChange}
            />

            {/* Logout */}
            <button onClick={handleLogout} title="Cerrar sesión" style={{ width: 24, height: 24, background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M10.09 15.59L11.5 17L16.5 12L11.5 7L10.09 8.41L12.67 11H3V13H12.67L10.09 15.59ZM19 3H5C3.89 3 3 3.9 3 5V9H5V5H19V19H5V15H3V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z" fill="#2B50AF"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Expired banner */}
        {subscriptionStatus.isExpired && (
          <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "8px 20px", fontSize: 12, fontWeight: 600 }}>
            ⚠️ Modo lectura: Acceso gratuito vencido
          </div>
        )}
      </header>

      {/* ══════════════════════════════════════════════
          HEADER — MOBILE (Figma spec)
      ══════════════════════════════════════════════ */}
      <header className="aorra-header aorra-header-mobile" style={{
        display:       "none", /* shown via media query */
        flexDirection: "column",
        alignItems:    "center",
        padding:       "14px 12px",
        gap:           8,
        width:         "100%",
        background:    "#FFFFFF",
        boxShadow:     "0px 4px 8px rgba(47, 95, 151, 0.05)",
        borderRadius:  "0px 0px 24px 24px",
        zIndex:        50,
        flexShrink:    0,
        boxSizing:     "border-box",
        position:      "sticky",
        top:           0,
      }}>
        <div style={{
          display:       "flex",
          flexDirection: "row",
          alignItems:    "center",
          padding:       "0px 4px",
          width:         "100%",
          boxSizing:     "border-box",
        }}>
          <div className="mobile-profile-row">
            <button className="mobile-profile-avatar" aria-label="Abrir perfil" onClick={() => setProfileOpen(true)}>
              {user?.avatar ? <img src={`/avatares/${user.avatar}`} alt="" /> : user?.name?.charAt(0).toUpperCase() || 'U'}
            </button>
            <span className="mobile-profile-name" title={user?.name}>{firstName}</span>
            <span className="mobile-profile-score" title={`Score ${healthScore ?? 0}/100 · ${scoreLabel}`} aria-label={`Score ${healthScore ?? 0} de 100: ${scoreLabel}`}>
              <span>Score</span> <strong style={{ color: scoreColor }}>{healthScore ?? 0}/100</strong>
            </span>
            <WorkspaceSelector />
          </div>

          {/* Right icons: Streak + Menu */}
          <div style={{
            display:    "flex",
            flexDirection: "row",
            alignItems: "center",
            gap:        12,
            flexShrink: 0,
          }}>
            <GameLivesBadge enabled={appSettings?.games?.public_enabled === true} />
            <StreakBadge
              profile={appSettings?.streaks_enabled === true ? streakProfile : null}
              compact
              preferenceLoading={streakPreferenceLoading}
              onParticipationChange={handleStreakParticipationChange}
            />

            {/* Hamburger menu */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              style={{ width: 24, height: 24, background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
              title="Menú"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M7 6H17" stroke="#2B50AF" strokeWidth="2" strokeLinecap="round"/>
                <path d="M4 12H20" stroke="#2B50AF" strokeWidth="2" strokeLinecap="round"/>
                <path d="M7 18H17" stroke="#2B50AF" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Expired banner mobile */}
        {subscriptionStatus.isExpired && (
          <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "6px 12px", fontSize: 11, fontWeight: 600, borderRadius: 8, width: "100%", boxSizing: "border-box", textAlign: "center" }}>
            ⚠️ Modo lectura: Acceso gratuito vencido
          </div>
        )}
      </header>

      {/* ══════════════════════════════════════════════
          MOBILE SIDEBAR OVERLAY (Figma spec — identical to desktop sidebar)
      ══════════════════════════════════════════════ */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
              zIndex: 9998,
            }}
          />
          {/* Sidebar Panel — slides from left */}
          <aside aria-label="Menú principal móvil" style={{
            position:      "fixed",
            top:           0,
            left:          0,
            bottom:        0,
            width:         229,
            display:       "flex",
            flexDirection: "column",
            alignItems:    "flex-end",
            paddingTop:    85,
            paddingBottom: 32,
            gap:           32,
            background:    "rgba(225, 233, 252, 0.87)",
            boxShadow:     "3px 7px 12.9px rgba(162, 162, 162, 0.1)",
            borderRadius:  "0px 25px 25px 0px",
            zIndex:        9999,
            overflowY:     "hidden",
            overflowX:     "hidden",
            animation:     "slideInLeft 0.25s ease-out",
            isolation:     "isolate",
          }}>
            {/* ── Nav items block ── */}
            <div style={{ width: 229, display: "flex", flexDirection: "column", alignItems: "flex-start", overflowY: "auto", minHeight: 0, flex: 1 }}>
              {navItems.map((item) => {
                const meta = NAV_ICONS[item.id] || { Icon: IconHome, label: item.id, showChevron: false };
                const { Icon, label, showChevron } = meta;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.end}
                    onClick={() => setMobileMenuOpen(false)}
                    style={{ textDecoration: "none", width: "100%", display: "block" }}
                  >
                    {({ isActive }) => (
                      <div style={{
                        display:        "flex",
                        flexDirection:  "row",
                        alignItems:     "center",
                        justifyContent: showChevron ? "space-between" : "flex-start",
                        padding:        "8px 32px",
                        gap:            12,
                        width:          "100%",
                        height:         isActive ? 59 : 52.77,
                        background:     isActive ? "#F0FDFD" : "transparent",
                        boxSizing:      "border-box",
                        transition:     "all 0.2s ease",
                      }}>
                        {/* Left side: Icon + label */}
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{
                            width:          36.77,
                            height:         36.77,
                            borderRadius:   "50%",
                            display:        "flex",
                            alignItems:     "center",
                            justifyContent: "center",
                            background:     isActive ? "#2B50AF" : "transparent",
                            color:          isActive ? "#FFFFFF" : "#2B50AF",
                            filter:         isActive ? "drop-shadow(2px 7px 17px rgba(47, 95, 151, 0.25))" : "none",
                            transition:     "all 0.25s ease",
                            flexShrink:     0,
                          }}>
                            <Icon />
                          </div>
                          <span style={{
                            fontFamily: "'Manrope', sans-serif",
                            fontSize:   14,
                            lineHeight: "19px",
                            color:      "#2B50AF",
                            fontWeight: isActive ? 700 : 400,
                          }}>
                            {label}
                          </span>
                        </div>

                        {/* Chevron for Mi Plan */}
                        {showChevron && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ opacity: isActive ? 1 : 0.5 }}>
                            <path d="M9 18L15 12L9 6" stroke="#2B50AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                    )}
                  </NavLink>
                );
              })}
            </div>

            <button
              type="button"
              data-testid="mobile-logout"
              onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
              style={{ width: "calc(100% - 32px)" }}
              className="mx-4 flex min-h-11 shrink-0 items-center gap-3 rounded-xl border border-[#2B50AF]/20 bg-white/70 px-4 py-3 text-sm font-bold text-[#2B50AF] hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#396BE9]"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 5H4V19H9M14 8L18 12L14 16M8 12H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Cerrar sesión
            </button>

            {/* ── Divider ── */}
            <div style={{ width: 229, height: 1, background: "rgba(43, 80, 175, 0.1)" }} />

            {/* ── Bottom: Último acceso ── */}
            <div style={{
              width:         229,
              padding:       "0px 32px",
              display:       "flex",
              flexDirection: "column",
              gap:           4,
              position:      "relative",
              zIndex:        10,
            }}>
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 400,
                fontSize:   16,
                lineHeight: "22px",
                color:      "#545454",
              }}>
                Último acceso
              </span>
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 400,
                fontSize:   14,
                lineHeight: "19px",
                color:      "#545454",
              }}>
                {lastAccess}
              </span>
            </div>

            {/* ── Watermark "ae" ── */}
            <div style={{
              position:       "absolute",
              bottom:         0,
              right:          0,
              width:          "100%",
              display:        "flex",
              alignItems:     "flex-end",
              justifyContent: "center",
              pointerEvents:  "none",
              zIndex:         0,
            }}>
              <img src="/cola-sliderbar.png" alt="" style={{ width: "140px", objectFit: "contain", opacity: 0.8 }} />
            </div>
          </aside>
        </>
      )}

      {/* ══════════════════════════════════════════════
          BODY: SIDEBAR + MAIN
      ══════════════════════════════════════════════ */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden", position: "relative", alignItems: "stretch" }}>

        {/* ────────────────────────────────────────────
            LEFT SIDEBAR
            bg: rgba(225,233,252,0.4)
            border-radius: 0 25px 25px 0 (right side only)
        ─────────────────────────────────────────── */}
        <aside className="aorra-sidebar" style={{
          width:          229,
          minWidth:       229,
          flexShrink:     0,
          display:        "flex",
          flexDirection:  "column",
          alignItems:     "flex-end",
          paddingTop:     85,          /* Lower the items (Figma: padding 85px 0px 32px) */
          paddingBottom:  32,
          gap:            0,
          background:     "rgba(225, 233, 252, 0.4)",
          boxShadow:      "3px 7px 12.9px rgba(162, 162, 162, 0.1)",
          borderRadius:   "0px 25px 25px 0px",
          position:       "sticky",
          top:            0,
          marginTop:      28,
          height:         "calc(100vh - 88px - 28px)",
          overflowY:      "auto",
          overflowX:      "hidden",
          zIndex:         10,
          alignSelf:      "flex-start",
        }}>

          {/* ── Nav items block ── */}
          <div style={{ width: 229, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            {navItems.map((item) => {
              const meta = NAV_ICONS[item.id] || { Icon: IconHome, label: item.id, showChevron: false };
              const { Icon, label, showChevron } = meta;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  style={{ textDecoration: "none", width: "100%", display: "block" }}
                >
                  {({ isActive }) => (
                    <div className={`aorra-nav-item ${isActive ? "active" : ""}`}>
                      {/* Left side: Icon inside active/hover-circle + label */}
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div className="aorra-icon-circle">
                          <Icon />
                        </div>
                        <span className="aorra-nav-label">
                          {label}
                        </span>
                      </div>

                      {/* Chevron for Mi Plan */}
                      {showChevron && (
                        <div className="aorra-chevron">
                          <IconChevronRight />
                        </div>
                      )}
                    </div>
                  )}
                </NavLink>
              );
            })}
          </div>

          {/* ── Spacer pushes divider + last access to the bottom ── */}
          <div style={{ flex: 1 }} />

          {/* ── Divider ── */}
          <div style={{ width: 229, height: 1, background: "rgba(43, 80, 175, 0.1)", marginBottom: 20 }} />

          {/* ── Bottom: Último acceso ── */}
          <div style={{
            width:         229,
            padding:       "0px 32px",
            display:       "flex",
            flexDirection: "column",
            gap:           4,
            position:      "relative",
            zIndex:        10,
          }}>
            <span style={{
              fontFamily:  "'Manrope', sans-serif",
              fontWeight:  400,
              fontSize:    16,
              lineHeight:  "22px",
              color:       "#545454",
            }}>
              Último acceso
            </span>
            <span style={{
              fontFamily:  "'Manrope', sans-serif",
              fontWeight:  400,
              fontSize:    14,
              lineHeight:  "19px",
              color:       "#545454",
            }}>
              {lastAccess}
            </span>
          </div>

          {/* ── Watermark "ae" ── */}
          <div style={{
            position:      "absolute",
            bottom:        0,
            right:         0,
            width:         "100%",
            display:       "flex",
            alignItems:    "flex-end",
            justifyContent:"center",
            pointerEvents: "none",
            zIndex:        0,
          }}>
            <img src="/cola-sliderbar.png" alt="" style={{ width: "140px", objectFit: "contain", opacity: 0.8 }} />
          </div>
        </aside>

        {/* ── MAIN CONTENT ── */}
        <main className="aorra-main" style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: "28px 32px 80px" }}>
          <WorkspaceStatus />
          <FreePlanUsage api={api} compact refreshKey={`${location.pathname}:${workspace?.active?.workspace_id || "personal"}`} />
          {!hiddenModules.includes('tableros') && <WorkspaceInvitationDialog />}
          <Outlet context={{ isExpired: workspace?.active ? !workspace.active.writable : subscriptionStatus.isExpired, setGlobalHealthScore: setHealthScore }} />
        </main>
      </div>

      {/* Styles: Animations & Hovers */}
      <style>{`
        /* Sidebar link hovers */
        .aorra-nav-item {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          padding: 8px 32px;
          gap: 12px;
          width: 100%;
          height: 52.77px;
          background: transparent;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          box-sizing: border-box;
          cursor: pointer;
        }

        .aorra-nav-item.active {
          height: 59px;
          background: #F0FDFD;
        }

        .aorra-nav-item:hover {
          background: #F0FDFD;
        }

        /* Icon Circle */
        .aorra-icon-circle {
          width: 36.77px;
          height: 36.77px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          color: #2B50AF;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          flex-shrink: 0;
        }

        .aorra-nav-item.active .aorra-icon-circle,
        .aorra-nav-item:hover .aorra-icon-circle {
          background: #2B50AF;
          color: #ffffff;
          filter: drop-shadow(2px 7px 17px rgba(47, 95, 151, 0.25));
        }

        /* Label styling and transition */
        .aorra-nav-label {
          font-family: 'Manrope', sans-serif;
          font-size: 14px;
          line-height: 19px;
          color: #2B50AF;
          transition: font-weight 0.2s ease, transform 0.2s ease;
          font-weight: 400;
        }

        .aorra-nav-item.active .aorra-nav-label {
          font-weight: 700;
        }

        .aorra-nav-item:hover .aorra-nav-label {
          transform: translateX(2px);
        }

        /* Chevron right opacity */
        .aorra-chevron {
          display: flex;
          align-items: center;
          opacity: 0.5;
          transition: opacity 0.2s ease;
        }
        .aorra-nav-item:hover .aorra-chevron,
        .aorra-nav-item.active .aorra-chevron {
          opacity: 1;
        }

        .aorra-streak-badge {
          min-width: 64px;
          height: 46px;
          padding: 1px 5px 1px 1px;
          border: 0;
          border-radius: 15px;
          background: transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 1px;
          color: #f4511e;
          cursor: pointer;
          flex-shrink: 0;
          transition: transform 160ms ease, background-color 160ms ease;
        }

        .aorra-streak-badge:hover {
          background: #F4F7FF;
          transform: translateY(-1px);
        }

        .aorra-streak-badge:focus-visible {
          outline: 3px solid rgba(57, 107, 233, 0.22);
          outline-offset: 2px;
        }

        .aorra-streak-badge img {
          width: 39px;
          height: 41px;
          object-fit: contain;
          flex-shrink: 0;
        }

        .aorra-streak-badge span {
          font-family: 'Manrope', sans-serif;
          font-size: 17px;
          line-height: 1;
          font-weight: 800;
          letter-spacing: -0.04em;
          color: #f4511e;
          white-space: nowrap;
        }

        .aorra-streak-badge-disabled {
          opacity: 0.52;
          filter: grayscale(0.65);
        }

        .aorra-streak-badge-disabled:hover {
          opacity: 0.8;
          filter: grayscale(0.25);
        }

        .aorra-streak-badge-compact {
          min-width: 51px;
          height: 36px;
          padding-right: 2px;
          border-radius: 12px;
        }

        .aorra-streak-badge-compact img {
          width: 30px;
          height: 32px;
        }

        .aorra-streak-badge-compact span {
          font-size: 14px;
        }

        /* Mobile sidebar slide-in animation */
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to   { transform: translateX(0); }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }

        @media (max-width: 768px) {
          .aorra-sidebar { display: none !important; }
          .aorra-header-desktop { display: none !important; }
          .aorra-header-mobile {
            display: flex !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            width: 100% !important;
            z-index: 50 !important;
          }
          .aorra-main {
            padding: 84px 16px 140px !important;
          }
        }
      `}
      </style>

      <ProfileSettings open={profileOpen} onOpenChange={setProfileOpen} />
      {location.pathname !== "/dashboard/juegos/salto" && <FloatingAIChat />}
    </div>
  );
};

export default DashboardLayout;
